import { useState, useEffect, useMemo } from 'react';
import {
  getProgrammes,
  getProgrammesByOpenDay
} from '../api/api-service';

// Empty Arrays
const toArray = (value) => (Array.isArray(value) ? value : []);

// Programme type constants 
const PROGRAMME_TYPE = {
  UNDERGRAD: 'U',
  POSTGRAD: 'P',
  ALL: 'A', // Both Undergrad and Postgrad
};

const DEFAULT_TOPIC_NAME = 'general talks and activities';

// Normalise type input. If empty/invalid => show all programmes.
const normaliseProgrammeType = (value) => {
  const t = String(value || '').trim().toUpperCase();
  return t === PROGRAMME_TYPE.UNDERGRAD || t === PROGRAMME_TYPE.POSTGRAD ? t : '';
};

export const useOpenDayData = (initialLang = 'en', programmeType = '', allProgrammes = []) => {
  const programmeTypeNorm = normaliseProgrammeType(programmeType);

  // 1. Language State
  const [lang, setLang] = useState(initialLang);

  // 2. State from useUndergradOpenDays
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 3. State from useOpenDayEvents
  const [eventsByOpenDay, setEventsByOpenDay] = useState({});
  const [topicsByOpenDay, setTopicsByOpenDay] = useState({});
  const [openIds, setOpenIds] = useState([]);
  const [detailsLoadingId, setDetailsLoadingId] = useState(null);
  const [detailsErrorId, setDetailsErrorId] = useState(null);
  const [searchByOpenDay, setSearchByOpenDay] = useState({});
  const [topicFilterByOpenDay, setTopicFilterByOpenDay] = useState({});
  const [pageByOpenDay, setPageByOpenDay] = useState({});

  // --- INITIAL DATA FETCH (Programmes & Topics) ---
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null); // clear previous error
      setItems([]);
      setEventsByOpenDay({});
      setTopicsByOpenDay({});
      setOpenIds([]);

      try {
        // We pass the 'lang' state directly to the API functions
        const progData = await getProgrammes(lang);
        setItems(toArray(progData));
      } catch (err) {
        console.error('loadInitialData failed', err);
        setError(err?.message || String(err) || 'Failed to load data');
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [lang]); // triggers the API refresh

  // --- EVENTS FETCHING LOGIC ---
  const fetchEventsForOpenDay = async (openDayId) => {
    try {
      setDetailsLoadingId(openDayId);
      setDetailsErrorId(null);

      // Pass the language to the API
      const topicsResponse = await getProgrammesByOpenDay(openDayId, lang);
      const topicsArr = toArray(topicsResponse);

      // Store topics for this open day
      setTopicsByOpenDay((prev) => ({ ...prev, [openDayId]: topicsArr }));

      const defaultTopic = topicsArr.find(
        (topic) =>
          String(topic?.name || '').trim().toLowerCase() === DEFAULT_TOPIC_NAME
      );

      setTopicFilterByOpenDay((prev) => {
        if (Object.prototype.hasOwnProperty.call(prev, openDayId)) return prev;

        return {
          ...prev,
          [openDayId]: defaultTopic ? String(defaultTopic.id) : 'all',
        };
      });

      const flatEvents = topicsArr.flatMap((topic) => {
        if (!topic) return [];
        if (!Array.isArray(topic.programs)) return [];
        return topic.programs.map((program) => ({
          ...program,
          __topicName: topic.name,
          __topicId: String(topic.id),
        }));
      });

      setEventsByOpenDay((prev) => ({ ...prev, [openDayId]: flatEvents }));
      setPageByOpenDay((prev) => ({ ...prev, [openDayId]: 1 }));
    } catch (err) {
      setDetailsErrorId(openDayId);
      setEventsByOpenDay((prev) => ({ ...prev, [openDayId]: [] }));
    } finally {
      setDetailsLoadingId(null);
    }
  };

  // --- HANDLERS ---
  const handleAccordionClick = (openDayId) => {
    const isOpenNow = openIds.includes(openDayId);
    setOpenIds((prev) =>
      isOpenNow ? prev.filter((id) => id !== openDayId) : [...prev, openDayId]
    );

    // If closing OR already fetched OR currently fetching, do nothing
    if (isOpenNow || eventsByOpenDay[openDayId] || detailsLoadingId === openDayId) return;

    fetchEventsForOpenDay(openDayId);
  };

  const handleSearchChange = (openDayId, value) => {
    setSearchByOpenDay((prev) => ({ ...prev, [openDayId]: value }));
    setPageByOpenDay((prev) => ({ ...prev, [openDayId]: 1 }));
  };

  const handleTopicFilterChange = (openDayId, topicId) => {
    setTopicFilterByOpenDay((prev) => ({ ...prev, [openDayId]: String(topicId || 'all') }));
    setPageByOpenDay((prev) => ({ ...prev, [openDayId]: 1 }));
  };

  const handlePageChange = (openDayId, newPage) => {
    setPageByOpenDay((prev) => ({ ...prev, [openDayId]: newPage }));
  };

  // --- DERIVED DATA ---
  // Filter open days by their type field (U, P, or A)
  const displayItems = useMemo(() => {
    if (!programmeTypeNorm) return items;
    
    return items.filter((item) => {
      const itemType = (item.type || '').toUpperCase();
      // Show if open day type matches requested type OR is type 'A' (all/both)
      return itemType === programmeTypeNorm || itemType === PROGRAMME_TYPE.ALL;
    });
  }, [items, programmeTypeNorm]);

  return {
    // Language control
    lang,
    setLang,

    // Data
    items, // raw (unfiltered)
    displayItems, // filtered by programmeType (or all if '')
    topicsByOpenDay,
    eventsByOpenDay,

    // Status
    loading,
    error,
    detailsLoadingId,
    detailsErrorId,

    // UI State
    openIds,
    searchByOpenDay,
    topicFilterByOpenDay,
    pageByOpenDay,

    // Methods
    handleAccordionClick,
    fetchEventsForOpenDay,
    handleSearchChange,
    handleTopicFilterChange,
    handlePageChange,

    // Derived helpers
    isSingleOpenDay: displayItems.length === 1,

    // Optional: expose constants if pages want them
    PROGRAMME_TYPE,
  };
};
